import { Fragment, useEffect, useRef, useState, type DragEvent } from 'react';
import { NavLink } from 'react-router-dom';
import { employees as api } from '@/api/endpoints';
import type { Employee, DwuConfirmRow } from '@/types';
import { Icon } from '@/components/Icon';
import { SearchableSelect } from '@/components/SearchableSelect';
import { validateUploadSize } from '@/lib/uploadLimits';
import { UploadProgressBar } from '@/components/UploadProgress';

const IMG_RE = /\.(jpe?g|png|webp|heic|pdf)$/i;
const hhmm = (t?: string | null) => (t ? t.slice(0, 5) : '');
const CONCURRENCY = 3;   // ile zdjęć czytamy równolegle (kompromis: postęp widoczny, a nie po jednym)

/** Poniedziałek tygodnia zawierającego datę YYYY-MM-DD (leksykalnie bezpieczne — bez new Date(str)). */
function mondayOf(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  const off = (dt.getDay() + 6) % 7;
  const mon = new Date(y, m - 1, d - off);
  return `${mon.getFullYear()}-${String(mon.getMonth() + 1).padStart(2, '0')}-${String(mon.getDate()).padStart(2, '0')}`;
}

/** Rekurencyjnie zbiera pliki z upuszczonego katalogu (webkitGetAsEntry). */
async function filesFromDataTransfer(dt: DataTransfer): Promise<File[]> {
  const items = dt.items ? Array.from(dt.items) : [];
  const entries = items.map(i => (i as any).webkitGetAsEntry?.()).filter(Boolean);
  if (entries.length === 0) return Array.from(dt.files);
  const out: File[] = [];
  const walk = async (entry: any): Promise<void> => {
    if (entry.isFile) {
      await new Promise<void>(res => entry.file((f: File) => { out.push(f); res(); }, () => res()));
    } else if (entry.isDirectory) {
      const reader = entry.createReader();
      await new Promise<void>(res => {
        const readBatch = () => reader.readEntries(async (batch: any[]) => {
          if (!batch.length) { res(); return; }
          for (const e of batch) await walk(e);
          readBatch();
        }, () => res());
        readBatch();
      });
    }
  };
  for (const e of entries) await walk(e);
  return out;
}

type ItemStatus = 'queued' | 'reading' | 'ready' | 'saving' | 'saved' | 'failed';
interface EditRow { date: string; dayName: string; startTime: string; endTime: string; delegation: number; location: string; }
interface Item {
  id: number; file: File; filename: string; aiName: string | null;
  employeeId: number | ''; rows: EditRow[];
  provider: string; method: string; error: string | null;
  status: ItemStatus; statusMsg?: string; expanded: boolean;
}
interface Prog { phase: 'extract' | 'confirm'; done: number; total: number; }

/**
 * Masowy odczyt dwutygodniówek: drag&drop / cały katalog zdjęć → AI czyta nazwisko i godziny z każdej
 * tabeli → przypisanie do pracownika (auto + korekta) → zbiorcze zatwierdzenie do ewidencji.
 * Odczyt idzie plik po pliku (pula {@link CONCURRENCY}) z paskiem postępu i możliwością przerwania.
 */
export function DwutygodniowkaBulkPage() {
  const [people, setPeople] = useState<Employee[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [items, setItems] = useState<Item[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [prog, setProg] = useState<Prog | null>(null);
  const [uploadPct, setUploadPct] = useState<number | null>(null);   // null = brak wysyłki (postęp bajtowy bieżącego pliku)
  const [err, setErr] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [summary, setSummary] = useState<string | null>(null);
  const filesRef = useRef<HTMLInputElement>(null);
  const dirRef = useRef<HTMLInputElement>(null);
  const cancelRef = useRef(false);
  const abortRef = useRef<AbortController[]>([]);

  useEffect(() => { api.list('ACTIVE').then(setPeople).catch(e => setErr(e?.message ?? String(e))); }, []);

  const addFiles = (list: File[]) => {
    if (busy) return;   // nie zmieniaj listy w trakcie odczytu/zapisu
    const imgs = list.filter(f => IMG_RE.test(f.name) && f.size > 0);
    // Limit 100 MB/plik — odrzuć za duże zdjęcia, wpuść ważny podzbiór, pokaż komunikat
    const sizeErr = validateUploadSize(imgs);
    const accepted = imgs.filter(f => !validateUploadSize(f));
    setFiles(prev => {
      const seen = new Set(prev.map(f => f.name + f.size));
      return [...prev, ...accepted.filter(f => !seen.has(f.name + f.size))];
    });
    setItems(null); setSummary(null); setErr(sizeErr);
  };

  const onDrop = async (e: DragEvent) => {
    e.preventDefault(); setDragOver(false);
    if (busy) return;
    try { addFiles(await filesFromDataTransfer(e.dataTransfer)); }
    catch (e2) { setErr('Nie udało się odczytać upuszczonych plików: ' + (e2 instanceof Error ? e2.message : String(e2))); }
  };

  const updItem = (i: number, patch: Partial<Item>) => setItems(its => its ? its.map((it, idx) => idx === i ? { ...it, ...patch } : it) : its);
  const updRow = (i: number, ri: number, patch: Partial<EditRow>) =>
    setItems(its => its ? its.map((it, idx) => idx === i ? { ...it, rows: it.rows.map((r, rj) => rj === ri ? { ...r, ...patch } : r) } : it) : its);

  const cancel = () => { cancelRef.current = true; abortRef.current.forEach(c => { try { c.abort(); } catch { /* ignore */ } }); };

  const extractAll = async () => {
    if (files.length === 0) { setErr('Dodaj zdjęcia (drag&drop, katalog lub wybór plików)'); return; }
    const sent = files;                 // stała referencja — indeksy stabilne przez cały odczyt
    const total = sent.length;
    setErr(null); setSummary(null); cancelRef.current = false;
    const controllers = sent.map(() => new AbortController());
    abortRef.current = controllers;
    setItems(sent.map((f, i) => ({
      id: i, file: f, filename: f.name, aiName: null, employeeId: '', rows: [],
      provider: '', method: '', error: null, status: 'queued', expanded: false,
    })));
    setProg({ phase: 'extract', done: 0, total });
    setUploadPct(0);   // pasek bajtowy bieżącej wysyłki
    setBusy(true);

    let cursor = 0;
    const worker = async () => {
      while (!cancelRef.current) {
        const i = cursor++;
        if (i >= total) return;
        updItem(i, { status: 'reading' });
        try {
          const r = await api.dwutygodniowkaAutoExtract(sent[i], controllers[i].signal, p => setUploadPct(p.percent));
          updItem(i, {
            aiName: r.aiEmployeeName ?? null,
            employeeId: r.matchedEmployeeId ?? '',
            rows: (r.rows ?? []).map(row => ({
              date: row.date, dayName: row.dayName,
              startTime: hhmm(row.startTime), endTime: hhmm(row.endTime),
              delegation: row.delegation ?? 0, location: row.location ?? '',
            })),
            provider: r.provider ?? '', method: r.method ?? '', error: r.error ?? null,
            status: r.error ? 'failed' : 'ready', statusMsg: r.error ?? undefined,
          });
        } catch (e) {
          const aborted = cancelRef.current || (e instanceof DOMException && e.name === 'AbortError');
          updItem(i, { status: 'failed', statusMsg: aborted ? 'Przerwano' : (e instanceof Error ? e.message : String(e)) });
        } finally {
          setProg(p => (p ? { ...p, done: p.done + 1 } : p));
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, total) }, worker));

    if (cancelRef.current) {
      // pliki, których nie zdążyliśmy dotknąć — oznacz jako przerwane
      setItems(its => its ? its.map(it => (it.status === 'queued' || it.status === 'reading') ? { ...it, status: 'failed', statusMsg: 'Przerwano' } : it) : its);
      setSummary('Przerwano odczyt.');
    }
    setProg(null); setUploadPct(null); setBusy(false);
  };

  const confirmAll = async () => {
    if (!items) return;
    const total = items.filter(it => it.employeeId !== '' && it.rows.length > 0 && it.status !== 'saved').length;
    if (total === 0) return;
    setBusy(true); setErr(null); setSummary(null); cancelRef.current = false;
    setProg({ phase: 'confirm', done: 0, total });
    let ok = 0, skipped = 0, failed = 0;
    const next = [...items];
    for (let i = 0; i < next.length; i++) {
      if (cancelRef.current) break;
      const it = next[i];
      if (it.employeeId === '' || it.rows.length === 0 || it.status === 'saved') { skipped++; continue; }
      next[i] = { ...it, status: 'saving' }; setItems([...next]);
      setUploadPct(0);   // pasek bajtowy dla bieżącego pliku
      try {
        const startMonday = mondayOf(it.rows[0].date);
        const rows: DwuConfirmRow[] = it.rows.map(r => ({ date: r.date, startTime: r.startTime || null, endTime: r.endTime || null, delegation: r.delegation, location: r.location || null }));
        const res = await api.dwutygodniowkaConfirm(Number(it.employeeId), startMonday, rows, it.file, p => setUploadPct(p.percent));
        next[i] = { ...it, status: 'saved', statusMsg: `${res.created} dni` }; ok++;
      } catch (e) {
        next[i] = { ...it, status: 'failed', statusMsg: e instanceof Error ? e.message : String(e) }; failed++;
      }
      setItems([...next]);
      setProg(p => (p ? { ...p, done: p.done + 1 } : p));
    }
    setProg(null); setUploadPct(null); setBusy(false);
    setSummary(`Zatwierdzono ${ok}, pominięto ${skipped}${failed ? `, błędów ${failed}` : ''}${cancelRef.current ? ' (przerwano)' : ''}.`);
  };

  const assignable = items?.filter(it => it.employeeId !== '' && it.status !== 'saved' && it.rows.length > 0).length ?? 0;
  const reading = items?.filter(it => it.status === 'reading').map(it => it.filename) ?? [];
  const pct = prog && prog.total > 0 ? Math.round((prog.done / prog.total) * 100) : 0;

  return (
    <div className="max-w-[1200px]">
      <header className="flex items-end justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1>Dwutygodniówki — odczyt masowy</h1>
          <div className="text-ink-400 text-[13px]">Wgraj wiele zdjęć / cały katalog — AI odczyta i przypisze do pracowników</div>
        </div>
        <NavLink to="/dwutygodniowka" className="btn btn-secondary btn-sm">← Pojedyncza</NavLink>
      </header>

      {err && <div className="text-red-500 mb-3 text-[12px]">Błąd: {err}</div>}
      {summary && <div className="mb-3 text-[13px] p-3 bg-green-50 border border-green-200 rounded text-green-700"><Icon name="check" className="w-4 h-4 inline mr-1"/>{summary}</div>}

      {/* Drop zone */}
      <div
        onDragOver={e => { e.preventDefault(); if (!busy) setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={'card mb-4 border-2 border-dashed transition ' + (dragOver ? 'border-water-500 bg-water-50/40' : 'border-ink-200') + (busy ? ' opacity-60 pointer-events-none' : '')}
      >
        <div className="card-body text-center py-8">
          <Icon name="download" className="w-8 h-8 mx-auto text-ink-300 mb-2"/>
          <div className="text-[14px] text-ink-700 font-medium">Przeciągnij tu zdjęcia lub cały katalog</div>
          <div className="text-[12px] text-ink-400 mt-1 mb-3">JPG / PNG / PDF · dwutygodniówka jednego pracownika = jedno zdjęcie</div>
          <div className="flex gap-2 justify-center">
            <input ref={filesRef} type="file" accept="image/*,.pdf" multiple className="hidden" onChange={e => addFiles(Array.from(e.target.files ?? []))}/>
            <input ref={dirRef} type="file" className="hidden" onChange={e => addFiles(Array.from(e.target.files ?? []))} {...({ webkitdirectory: '', directory: '' } as any)}/>
            <button onClick={() => filesRef.current?.click()} disabled={busy} className="btn btn-secondary btn-sm"><Icon name="plus" className="w-3.5 h-3.5"/> Wybierz zdjęcia</button>
            <button onClick={() => dirRef.current?.click()} disabled={busy} className="btn btn-secondary btn-sm"><Icon name="plus" className="w-3.5 h-3.5"/> Wybierz katalog</button>
          </div>
          {files.length > 0 && (
            <div className="mt-3 text-[12px] text-ink-600">
              {files.length} {files.length === 1 ? 'plik' : 'plików'} gotowych.
              {!busy && <button onClick={() => { setFiles([]); setItems(null); }} className="ml-2 text-ink-400 hover:text-red-500 underline">wyczyść</button>}
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-2 mb-4 items-center flex-wrap">
        {!prog && (
          <button onClick={extractAll} disabled={files.length === 0} className="btn btn-primary btn-sm">
            <Icon name="sparkles" className="w-3.5 h-3.5"/> Odczytaj wszystkie ({files.length})
          </button>
        )}
        {items && !prog && (
          <button onClick={confirmAll} disabled={assignable === 0} className="btn btn-primary btn-sm">
            <Icon name="check" className="w-3.5 h-3.5"/> Zatwierdź wszystkie ({assignable})
          </button>
        )}
        {prog && (
          <button onClick={cancel} className="btn btn-secondary btn-sm">Przerwij</button>
        )}
      </div>

      {/* Pasek postępu — odczyt AI / zapis */}
      {prog && (
        <div className="card mb-4"><div className="card-body py-3">
          <div className="flex items-center justify-between text-[12px] mb-1.5">
            <span className="font-medium text-ink-700">
              {prog.phase === 'extract' ? 'Odczyt AI' : 'Zapisywanie do ewidencji'} — {prog.done} / {prog.total}
            </span>
            <span className="font-mono text-ink-500">{pct}%</span>
          </div>
          <div className="h-2 bg-ink-100 rounded overflow-hidden">
            <div className="h-full bg-water-500 transition-all duration-300 ease-out" style={{ width: `${pct}%` }} />
          </div>
          {prog.phase === 'extract' && reading.length > 0 && (
            <div className="mt-2 text-[11px] text-ink-500 truncate">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-water-500 animate-pulse mr-1.5 align-middle"/>
              Czytam: {reading.slice(0, 3).join(', ')}{reading.length > 3 ? ` +${reading.length - 3}` : ''}
            </div>
          )}
          {/* Postęp bajtowy bieżącej wysyłki pliku */}
          {uploadPct !== null && (
            <div className="mt-2">
              <UploadProgressBar percent={uploadPct} label="Wysyłanie pliku…" compact />
            </div>
          )}
        </div></div>
      )}

      {items && (
        <div className="card"><div className="card-body !p-0">
          <table className="table-hw">
            <thead><tr><th>Plik</th><th>Odczytany</th><th>Pracownik</th><th>Dni</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {items.map((it, i) => (
                <Fragment key={it.id}>
                  <tr className="hover:bg-ink-50">
                    <td className="text-[12px] text-ink-700">
                      <div className="truncate max-w-[220px]" title={it.filename}>{it.filename}</div>
                    </td>
                    <td className="text-[12px] text-ink-500">{it.aiName ?? '—'}</td>
                    <td className="min-w-[220px]">
                      <SearchableSelect value={it.employeeId === '' ? '' : String(it.employeeId)} onChange={v => updItem(i, { employeeId: v ? Number(v) : '' })} placeholder="— przypisz —" disabled={busy}>
                        <option value="">— przypisz —</option>
                        {people.map(p => <option key={p.id} value={p.id}>{p.fullName}</option>)}
                      </SearchableSelect>
                    </td>
                    <td className="text-[12px] font-mono text-ink-600">{it.rows.filter(r => r.startTime).length}/{it.rows.length || '—'}</td>
                    <td>
                      {it.status === 'queued' && <span className="text-[11px] text-ink-400">W kolejce…</span>}
                      {it.status === 'reading' && <span className="pill pill-water animate-pulse">Odczyt AI…</span>}
                      {it.status === 'saved' && <span className="pill pill-green">Zapisano{it.statusMsg ? ` · ${it.statusMsg}` : ''}</span>}
                      {it.status === 'failed' && <span className="pill pill-red" title={it.statusMsg}>{it.statusMsg === 'Przerwano' ? 'Przerwano' : 'Błąd'}</span>}
                      {it.status === 'saving' && <span className="pill pill-amber">Zapisywanie…</span>}
                      {it.status === 'ready' && (it.employeeId === '' ? <span className="pill pill-amber">Przypisz pracownika</span> : <span className="pill pill-water">Gotowe</span>)}
                    </td>
                    <td className="text-right">
                      {it.rows.length > 0 && <button onClick={() => updItem(i, { expanded: !it.expanded })} disabled={busy} className="btn btn-ghost btn-icon text-ink-400" title="Edytuj godziny"><Icon name="chevron" className={'w-3.5 h-3.5 transition ' + (it.expanded ? 'rotate-180' : '')}/></button>}
                    </td>
                  </tr>
                  {it.expanded && (
                    <tr>
                      <td colSpan={6} className="!p-0 bg-ink-50/40">
                        <div className="p-3 overflow-x-auto">
                          <table className="table-hw">
                            <thead><tr><th>Dzień</th><th>Data</th><th>Rozp.</th><th>Zak.</th><th>Deleg.</th><th>Lokalizacja</th></tr></thead>
                            <tbody>
                              {it.rows.map((r, ri) => (
                                <tr key={r.date}>
                                  <td className="text-[12px]">{r.dayName}</td>
                                  <td className="font-mono text-[12px] text-ink-500">{r.date}</td>
                                  <td><input type="time" value={r.startTime} disabled={busy} onChange={e => updRow(i, ri, { startTime: e.target.value })} className="input !py-1 !px-2 w-[104px] font-mono text-[12px]"/></td>
                                  <td><input type="time" value={r.endTime} disabled={busy} onChange={e => updRow(i, ri, { endTime: e.target.value })} className="input !py-1 !px-2 w-[104px] font-mono text-[12px]"/></td>
                                  <td><input type="number" step="0.5" min="0" max="1" value={r.delegation} disabled={busy} onChange={e => updRow(i, ri, { delegation: Number(e.target.value) })} className="input !py-1 !px-2 w-[64px] font-mono text-[12px] text-right"/></td>
                                  <td><input value={r.location} disabled={busy} onChange={e => updRow(i, ri, { location: e.target.value })} className="input !py-1 !px-2 text-[12px]"/></td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div></div>
      )}
    </div>
  );
}
